import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { getStaticText } from '../../../../utils/constants';
import { getBenchmarksLabel, getDemographicsLabel, manipulateHeatmapTitle } from '../../../../utils/functions';
import { findIndExpHeatmapHeaderIndex } from '../indExperiencePublicSector';
import { ExcelFilterRows, getExcelFileName, getIeScoreCell, isNSizeTitle, writeColoredExcel } from '../excelUtils';

const TABLE_ID = 'ieHeatmapExcelTable';

function Excel({
    headerToUse,
    dataToUse,
    header,
    practiceCategories,
    enpsHeatmap,
    enpsHeader,
    filters,
    selectedDemographics,
    defaultSettings,
    headerTranslationMap,
    setSelectedDownload,
}) {
    const {
        lang = '1033',
        ohid = '',
        engagement = '',
        apply_employee_exp_benchmark = false,
        quartileColors = [],
    } = defaultSettings;
    const staticText = getStaticText(lang);
    const { SITE_TEXT, NON_PROFIT_SURVEY } = staticText;
    const {
        NEGATIVE_PRACTICES = [],
        EMPLOYEE_NET_PROMOTER_SCORE,
        HEATMAP,
        EX_ENPS_HEADER = [],
        DEMO_SELECTED_OPTIONS,
        BENCHMARK_FILTERS,
        BENCHMARK_NA,
    } = SITE_TEXT;
    const isNonProfit = engagement === NON_PROFIT_SURVEY;
    const { demographics = [], benchmarks = [], ex_benchmarks = [] } = filters[0] || {};
    const selectedGroup = demographics.find(({ code }) => code === selectedDemographics) || {};
    const selectedLabel = selectedGroup.label || '';
    const demoFilterLabels = getDemographicsLabel(demographics);
    const benchLabel = getBenchmarksLabel(benchmarks);
    const exBenchLabel = apply_employee_exp_benchmark ? getBenchmarksLabel(ex_benchmarks) || '' : BENCHMARK_NA;

    const getExport = () => {
        setSelectedDownload('');
        return writeColoredExcel(TABLE_ID, getExcelFileName('Employee_Experience_Heatmap', defaultSettings), {
            preventDateCoercion: true,
        });
    };

    useEffect(() => {
        getExport();
        // eslint-disable-next-line
    }, []);

    const renderHeatmapRows = () => {
        const rows = [];
        practiceCategories.forEach(({ children: child = [], header_slug: headerSlug = [] }) => {
            child.forEach((elem, childIndex) => {
                const index = findIndExpHeatmapHeaderIndex(elem, childIndex, headerSlug, header, headerTranslationMap);
                if (index === -1 || dataToUse[index] === undefined) return;
                const title = manipulateHeatmapTitle(header[index]);
                const isNegativeTrait = NEGATIVE_PRACTICES.includes(title);
                const isN = isNSizeTitle(header[index]) || isNSizeTitle(title);
                const subItems = dataToUse[index] || [];
                rows.push(
                    <tr key={`hm-${title}-${index}`}>
                        <td>bold#{title}</td>
                        {subItems.map((cell, cellIndex) => {
                            const { score, quartile = '' } = cell || {};
                            return (
                                <td key={`${title}-${cellIndex}`}>
                                    {getIeScoreCell({
                                        score,
                                        quartile,
                                        isNegativeTrait,
                                        isN,
                                        applyBenchmark: apply_employee_exp_benchmark,
                                        quartileColors,
                                        isNonProfit,
                                        ohid,
                                    })}
                                </td>
                            );
                        })}
                    </tr>
                );
            });
        });
        return rows;
    };

    const renderEnpsRows = () => {
        if (!enpsHeatmap.length) return null;
        return (
            <>
                <tr>
                    <td>
                        bold#{EMPLOYEE_NET_PROMOTER_SCORE} (eNPS) {HEATMAP}
                    </td>
                </tr>
                <tr>
                    <th />
                    {(enpsHeader[0] || []).map((val, i) => (
                        <th key={`enps-h-${val}-${i}`}>bold#{manipulateHeatmapTitle(val)}#center</th>
                    ))}
                </tr>
                {enpsHeatmap.map((subItems, parentI) => {
                    const title = EX_ENPS_HEADER[parentI] || '';
                    const isN = isNSizeTitle(title);
                    const skipEnpsFill = [2, 3, 4].includes(parentI);
                    return (
                        <tr key={`enps-${title}-${parentI}`}>
                            <td>bold#{title}</td>
                            {(subItems || []).map((cell, cellIndex) => {
                                const { score, quartile = '' } = cell || {};
                                return (
                                    <td key={`${title}-${cellIndex}`}>
                                        {getIeScoreCell({
                                            score,
                                            quartile,
                                            isN: isN || skipEnpsFill,
                                            applyBenchmark: true,
                                            quartileColors,
                                            forceQuartile: true,
                                        })}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </>
        );
    };

    return (
        <div>
            <table id={TABLE_ID} className="hideTable">
                <ExcelFilterRows
                    selectedLabel={selectedLabel}
                    demoFilterLabels={demoFilterLabels}
                    benchLabel={benchLabel}
                    exBenchLabel={exBenchLabel}
                    staticText={{ DEMO_SELECTED_OPTIONS, BENCHMARK_FILTERS }}
                />
                <tbody>
                    <tr>
                        <th />
                        {headerToUse.map((val, i) => (
                            <th key={`h-${val}-${i}`}>bold#{manipulateHeatmapTitle(val)}#center</th>
                        ))}
                    </tr>
                    {renderHeatmapRows()}
                    {renderEnpsRows()}
                </tbody>
            </table>
        </div>
    );
}

Excel.propTypes = {
    headerToUse: PropTypes.array.isRequired,
    dataToUse: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired,
    practiceCategories: PropTypes.array.isRequired,
    enpsHeatmap: PropTypes.array,
    enpsHeader: PropTypes.array,
    filters: PropTypes.array.isRequired,
    selectedDemographics: PropTypes.string.isRequired,
    defaultSettings: PropTypes.object.isRequired,
    headerTranslationMap: PropTypes.object,
    setSelectedDownload: PropTypes.func.isRequired,
};

Excel.defaultProps = {
    enpsHeatmap: [],
    enpsHeader: [],
    headerTranslationMap: {},
};

export default Excel;
